import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { ChevronRight, Calendar, Home as HomeIcon, Package, Star, Mail, Phone, MapPin, Instagram } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const Home = ({ user }) => {
  const navigate = useNavigate();
  const [topReviews, setTopReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewStartIndex, setReviewStartIndex] = useState(0);
  const currentYear = new Date().getFullYear();

  const howItWorks = [
    {
      icon: <Calendar className="w-12 h-12 text-[#d4a574]" />,
      title: "Book a Designer",
      description: "Choose your preferred designer & schedule a visit"
    },
    {
      icon: <HomeIcon className="w-12 h-12 text-[#d4a574]" />,
      title: "Get a Design Consultation at Home",
      description: "Discuss style, measurements & fabric"
    },
    {
      icon: <Package className="w-12 h-12 text-[#d4a574]" />,
      title: "Receive Your Custom Outfit",
      description: "Tailored & delivered to your doorstep"
    }
  ];

  const teamMembers = [
    {
      name: "Vishal Mishra",
      title: "Founder & CEO",
      bio: "Driving vision, partnerships, and luxury innovation.",
      initials: "V",
    },
    {
      name: "Akash",
      title: "Co‑Founder & CTO",
      bio: "Building the tech backbone behind the experience.",
      initials: "A",
    },
    {
      name: "Bhoomika",
      title: "Head of Design",
      bio: "Crafting exceptional designs with attention to detail.",
      initials: "B",
    }
  ];

  useEffect(() => {
    const fetchTopReviews = async () => {
      try {
        // Ask backend for more than 6 so carousel can rotate
        const response = await axios.get(`${API_URL}/reviews/top/all`, {
          params: { limit: 20 },
        });
        setTopReviews(response.data || []);
      } catch (error) {
        console.error('Error fetching top reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchTopReviews();
  }, []);

  // Rotate reviews so new ones keep coming in while older ones move back
  useEffect(() => {
    if (!topReviews || topReviews.length <= 1) return;
    const id = setInterval(() => {
      setReviewStartIndex((prev) => (prev + 1) % topReviews.length);
    }, 5000); // rotate every 5 seconds
    return () => clearInterval(id);
  }, [topReviews.length]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1410] via-[#0a0a0a] to-[#0a0a0a] opacity-80"></div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d4a574] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4a574] rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center fade-in">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
            Your Personal Designer,
            <br />
            <span className="gradient-text">At Your Doorstep</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-12 max-w-3xl mx-auto" data-testid="hero-subtitle">
            Book a designer to create custom outfits at home. Experience luxury fashion tailored to your style.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate('/designers')}
              className="btn-primary flex items-center gap-2"
              data-testid="book-designer-btn"
            >
              Book a Designer
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/products')}
              className="btn-secondary"
              data-testid="explore-designers-btn"
            >
              Explore Products
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-[#0f0f0f]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-16 gradient-text">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div
                key={index}
                className="glass-card p-8 text-center card-hover"
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <div className="flex justify-center mb-6">{step.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-[#f0d9b5]">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center glass-card p-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Experience Luxury Fashion?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Join thousands of satisfied customers who trust Wearoclub for their custom fashion needs.
          </p>
          <button
            onClick={() => navigate('/designers')}
            className="btn-primary"
            data-testid="cta-get-started-btn"
          >
            Get Started
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-[#0f0f0f]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text">
            Loved by Our Customers
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            A glimpse of what people say about their experience with Wearoclub.
          </p>
          {reviewsLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="spinner"></div>
            </div>
          ) : topReviews.length === 0 ? (
            <p className="text-center text-gray-500">No reviews yet. Book a designer to be the first!</p>
          ) : (
            <div className="max-w-3xl mx-auto">
              {(() => {
                const review = topReviews[reviewStartIndex % topReviews.length];
                return (
                  <div
                    key={review.id || reviewStartIndex}
                    className="glass-card p-8 flex flex-col h-full transition-transform duration-500 ease-out"
                    data-testid="testimonial-card-active"
                  >
                    <div className="flex items-center gap-2 mb-4 text-[#d4a574]">
                      {[1, 2, 3, 4, 5].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? 'text-[#d4a574] fill-current' : 'text-gray-600'}`}
                        />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-6 leading-relaxed">
                      &ldquo;{review.comment}&rdquo;
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <p className="font-semibold text-[#f0d9b5]">
                        {review.user_name || 'Wearoclub Customer'}
                      </p>
                      {topReviews.length > 1 && (
                        <span className="text-xs text-gray-500">
                          {reviewStartIndex + 1} / {topReviews.length}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text">
            Meet the Team
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            Wearoclub is built by a small team of founders, co‑founders and developers who care deeply about fashion, technology and customer experience.
          </p>
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"> */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="glass-card p-6 text-center card-hover h-full"
                data-testid={`team-member-${index + 1}`}
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#d4a574]/20 flex items-center justify-center text-2xl font-bold text-[#d4a574]">
                  {member.initials}
                </div>
                <h3 className="text-lg font-semibold text-[#f0d9b5] mb-1">
                  {member.name}
                </h3>
                <p className="text-sm text-[#d4a574] mb-3">{member.title}</p>
                <p className="text-sm text-gray-400 leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f0f0f] px-4 pt-16 pb-8 border-t border-[#d4a574]/20" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            <div>
              <h4 className="text-lg font-semibold text-[#f0d9b5] mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button onClick={() => navigate('/')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Home
                </button>
                <button onClick={() => navigate('/designers')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Designers
                </button>
                <button onClick={() => navigate('/products')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Products
                </button>
                <button onClick={() => navigate('/dashboard')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  My Bookings
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-[#f0d9b5] mb-4">Support</h4>
              <div className="space-y-3 text-gray-400">
                <button onClick={() => navigate('/support')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Contact Us
                </button>
                <button onClick={() => navigate('/support')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Home Visit Booking Support
                </button>
                <button onClick={() => navigate('/support')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Payments & Order Assistance
                </button>
                <button onClick={() => navigate('/privacy-policy')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Privacy Policy
                </button>
                <button onClick={() => navigate('/terms-and-conditions')} className="block text-gray-400 hover:text-[#d4a574] transition-colors">
                  Terms & Conditions
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-[#f0d9b5] mb-4">Contact</h4>
              <div className="space-y-4 text-gray-400">
                <a href="mailto:support@wearoclub.com" className="flex items-start gap-3 hover:text-[#d4a574] transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 text-[#d4a574]" />
                  <span>support@wearoclub.com</span>
                </a>
                <a href="mailto:hello@wearoclub.com" className="flex items-start gap-3 hover:text-[#d4a574] transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 text-[#d4a574]" />
                  <span>hello@wearoclub.com</span>
                </a>
                {/* <a href="tel:+919999999999" className="flex items-start gap-3 hover:text-[#d4a574] transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 text-[#d4a574]" />
                  <span>+91 99999 99999</span>
                </a>  */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-0.5 text-[#d4a574]" />
                  <span>BS -90 near society park sector 70 noida</span>
                </div>
                <a
                  href="https://instagram.com/wearoclub"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 hover:text-[#d4a574] transition-colors"
                >
                  <Instagram className="w-5 h-5 mt-0.5 text-[#d4a574]" />
                  <span>Follow us on Instagram</span>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-[#d4a574]/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <p>&copy; {currentYear} Wearoclub. All rights reserved.</p>
            <p>Experience Luxury fashion, from comfort of your home.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
